import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import type { Attachment, AttachmentEntityType } from '@/types/crm';

interface AttachmentStore {
  attachments: Attachment[];
  isLoading: boolean;
  isUploading: boolean;
  fetchAttachments: (entityType: AttachmentEntityType, entityId: string) => Promise<Attachment[]>;
  uploadAttachment: (
    entityType: AttachmentEntityType,
    entityId: string,
    file: File
  ) => Promise<Attachment | null>;
  deleteAttachment: (id: string, storagePath: string) => Promise<void>;
}

export const useAttachmentStore = create<AttachmentStore>((set, get) => ({
  attachments: [],
  isLoading: false,
  isUploading: false,

  fetchAttachments: async (entityType: AttachmentEntityType, entityId: string) => {
    set({ isLoading: true });
    try {
      const { data, error } = await supabase
        .from('crm_attachments')
        .select('*')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ attachments: data || [] });
      return data || [];
    } finally {
      set({ isLoading: false });
    }
  },

  uploadAttachment: async (
    entityType: AttachmentEntityType,
    entityId: string,
    file: File
  ) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    set({ isUploading: true });
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${entityType}/${entityId}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('crm-attachments')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('crm-attachments')
        .getPublicUrl(fileName);

      const { data: attachment, error: dbError } = await supabase
        .from('crm_attachments')
        .insert({
          entity_type: entityType,
          entity_id: entityId,
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          file_url: publicUrl,
          storage_path: fileName,
          uploaded_by: user.id,
        })
        .select()
        .single();

      if (dbError) throw dbError;

      await get().fetchAttachments(entityType, entityId);
      return attachment;
    } finally {
      set({ isUploading: false });
    }
  },

  deleteAttachment: async (id: string, storagePath: string) => {
    const { attachments } = get();
    const attachment = attachments.find(a => a.id === id);

    await supabase.storage
      .from('crm-attachments')
      .remove([storagePath]);

    const { error } = await supabase
      .from('crm_attachments')
      .delete()
      .eq('id', id);

    if (error) throw error;

    if (attachment) {
      await get().fetchAttachments(attachment.entity_type, attachment.entity_id);
    }
  },
}));
